import { ITenantConfigResponse } from '@inshuwa-client/models';
import { deriveDarkPrimary, deriveInteractionTints, resolveOnPrimary } from '../utils/color-palette';

// Keep in sync with the custom properties libs/shared-styles/theme.less actually declares.
// Deliberately a closed allow-list, not "apply whatever keys the backend sends" - an anonymous,
// domain-keyed endpoint is not a safe place to accept arbitrary CSS custom property names.
const KNOWN_CSS_TOKENS = new Set([
  '--table-row-radius',
  '--table-header-radius',
  // Optional dark-mode surfaces. Only read by the .dark rules in global-styles.scss, so setting
  // them in light mode is inert - no need to gate these on `dark`.
  '--dark-surface-color',
  '--dark-card-color',
  '--dark-on-surface-color',
  '--dark-on-surface-muted-color',
  '--dark-table-header-bg',
  '--dark-table-header-color',
]);

/**
 * Which CSS custom properties to set for a given config.
 *
 * `dark` is a parameter rather than a `.dark {}` rule because these land as inline styles on
 * <html>, and an inline style beats every stylesheet rule on the same element - the dark-surface
 * brand colour can only be swapped in here.
 */
export function computeThemeProperties(
  config: ITenantConfigResponse,
  dark = false,
): Record<string, string> {
  // An explicit dark colour from brand setup always wins; deriving is the fallback so tenants who
  // never configure one still get a readable dark theme instead of navy-on-black.
  const primaryColor = dark
    ? config.darkPrimaryColor || deriveDarkPrimary(config.primaryColor)
    : config.primaryColor;
  const secondaryColor = config.secondaryColor || primaryColor;
  // Feeds the @primary-5/@primary-7 pins in theme.less and theme.dark.less, which is what makes
  // ng-zorro's hover and active states follow the tenant instead of the build's brand.
  const tints = deriveInteractionTints(primaryColor, dark);

  const properties: Record<string, string> = {
    '--primary-color': primaryColor,
    '--primary-color-hover': tints.hover,
    '--primary-color-active': tints.active,
    '--primary-text-color': dark
      ? resolveOnPrimary(primaryColor, config.primaryTextColor)
      : config.primaryTextColor,
    '--secondary-color': dark && config.secondaryColor ? deriveDarkPrimary(secondaryColor) : secondaryColor,
  };

  if (config.fontFamily) {
    properties['--font-family'] = config.fontFamily;
  }

  for (const [key, value] of Object.entries(config.customProperties ?? {})) {
    if (KNOWN_CSS_TOKENS.has(key)) {
      properties[key] = value;
    }
  }

  return properties;
}

/** Which URL the favicon should use. */
export function resolveFaviconHref(config: ITenantConfigResponse): string | undefined {
  return config.faviconUrl || config.logoUrl || undefined;
}
